/**
 * useCompanies hook - TanStack Query
 * 
 * Constitution Section 19.1: State Management Separation
 * Constitution Section 19.2: Data Fetching Patterns
 * Constitution Section 19.4: API Response Validation (Zod Mandatory)
 * 
 * This hook manages server state for companies using React Query.
 * UI state (activeCompany) should be managed in Zustand store.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { getCompanies } from '@/api/endpoints/companies';
import { isTokenReady } from '@/api/client';
import type { Company } from '@/schemas/company';

/**
 * Query key for companies
 */
export const companiesQueryKey = ['companies'] as const;

/**
 * CRITICAL: Stable empty array reference to prevent infinite re-renders
 * When query.data is undefined during loading, we return this stable reference
 * instead of creating a new [] on every render.
 */
const EMPTY_COMPANIES: Company[] = [];

/**
 * Hook to fetch companies using TanStack Query
 * 
 * Features:
 * - Automatically enabled when user is authenticated
 * - 5 minute stale time (data considered fresh for 5 minutes)
 * - Zod validation at API boundary
 * - Type-safe return values
 * 
 * @returns React Query result with companies data
 */
export function useCompanies() {
  const { isAuthenticated } = useAuth0();

  const query = useQuery({
    queryKey: companiesQueryKey,
    queryFn: async () => {
      // getCompanies already handles Zod parsing at the boundary
      return await getCompanies();
    },
    enabled: isAuthenticated && isTokenReady(), // Wait for both auth AND token getter
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2, // Retry twice (token may still be loading)
    retryDelay: 1000, // 1s between retries
  });

  return {
    // CRITICAL: Use stable empty array reference to prevent infinite re-renders
    // query.data ?? [] creates a new array on every render, causing useEffect loops
    companies: query.data ?? EMPTY_COMPANIES,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    isError: query.isError,
    isSuccess: query.isSuccess,
  };
}

/**
 * Type-safe return type
 */
export type UseCompaniesReturn = ReturnType<typeof useCompanies>;
